import React from 'react';
import { Cpu, Layers, Rocket } from 'lucide-react';
import { SiteSection } from '../../components/site';
import type { SolutionPremiumSections, SolutionStat } from './types';
import { useTabPanel } from './useTabPanel';

const STEP_ICONS = {
  connect: Cpu,
  build: Layers,
  launch: Rocket,
} as const;

export const SolutionManufacturingStack: React.FC<{
  pillars: SolutionPremiumSections['pillars'];
  stats: readonly SolutionStat[];
}> = ({ pillars, stats }) => {
  const { active, activeId, getPanelProps, getTabProps } = useTabPanel(pillars.items);
  const activeIndex = pillars.items.findIndex((item) => item.id === activeId);

  return (
    <SiteSection id="pillars" tone="muted" className="solution-pillars mf-stack">
      <header className="mf-stack__header">
        <p className="io-site-section__eyebrow">{pillars.eyebrow}</p>
        <h2 className="io-site-section__title">{pillars.title}</h2>
        <p className="io-site-section__subtitle">{pillars.subtitle}</p>
      </header>

      <div className="mf-stack__stats" aria-label="Platform highlights">
        {stats.map((stat) => (
          <div key={stat.label} className="mf-stack__stat">
            <span className="mf-stack__stat-value">{stat.value}</span>
            <span className="mf-stack__stat-label">{stat.label}</span>
          </div>
        ))}
      </div>

      <div className="mf-stack__shell">
        <div className="mf-stack__decor" aria-hidden>
          <span className="mf-stack__orb mf-stack__orb--1" />
          <span className="mf-stack__orb mf-stack__orb--2" />
          <span className="mf-stack__grid" />
        </div>

        <div className="mf-stack__layout">
          <aside className="mf-stack__sidebar">
            <nav className="mf-stack__nav" role="tablist" aria-label="Product stack journey">
              <div className="mf-stack__nav-list">
                {pillars.items.map((pillar, index) => {
                  const selected = pillar.id === active.id;
                  const Icon = STEP_ICONS[pillar.id as keyof typeof STEP_ICONS] ?? Cpu;
                  const isPast = index < activeIndex;
                  const isLast = index === pillars.items.length - 1;

                  return (
                    <div
                      key={pillar.id}
                      className={`mf-stack__nav-item${selected ? ' is-active' : ''}${isPast ? ' is-complete' : ''}`}
                    >
                      <div className="mf-stack__nav-marker" aria-hidden>
                        <span className="mf-stack__nav-dot" />
                        {!isLast ? (
                          <span
                            className={`mf-stack__nav-connector${isPast || selected ? ' is-filled' : ''}`}
                          />
                        ) : null}
                      </div>

                      <button
                        type="button"
                        className={`mf-stack__nav-btn${selected ? ' is-active' : ''}`}
                        {...getTabProps(pillar, selected)}
                      >
                        <span className="mf-stack__nav-icon" aria-hidden>
                          <Icon size={17} strokeWidth={2} />
                        </span>
                        <span className="mf-stack__nav-label">{pillar.label}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </nav>
          </aside>

          <div className="mf-stack__panels">
            {pillars.items.map((pillar) => {
              const selected = pillar.id === active.id;

              return (
                <div
                  key={pillar.id}
                  className={`mf-stack__panel${selected ? ' mf-stack__panel--active' : ''}`}
                  {...getPanelProps(pillar)}
                >
                  <article
                    className="mf-stack__panel-card"
                    aria-label={pillar.image.alt}
                  >
                    <div className="mf-stack__panel-media" aria-hidden>
                      <img
                        src={pillar.image.src}
                        alt=""
                        width={pillar.image.width}
                        height={pillar.image.height}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="mf-stack__panel-glass">
                      <div className="mf-stack__panel-body">
                        <h3 className="mf-stack__panel-title">{pillar.title}</h3>
                        <ul className="mf-stack__features">
                          {pillar.items.map((item) => (
                            <li key={item.title} className="mf-stack__feature">
                              <span className="mf-stack__feature-title">{item.title}</span>
                              {item.description ? (
                                <span className="mf-stack__feature-desc">{item.description}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </article>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SiteSection>
  );
};
